import { Dayjs } from 'dayjs'
import { useMemo } from 'react'
import { UserAppointment } from '$api-client/types.gen'
import { ScheduledPerson } from './use-calendar-slots'
import { DateHelper } from '$utils/date'
import { getDaysOfWeek } from './use-week'

export type GetWorkingDaysOfWeekArgs = {
  date: Dayjs
  people?: ScheduledPerson[]
  appointments?: UserAppointment[]
  isLoading?: boolean
  enabled?: boolean
}

/**
 * A utility function to get the working days of the week based on the provided date, people, appointments, and loading state.
 * It filters out days where no groomers are working or there are no appointments.
 * @param date - The reference date for the week
 * @param people - Array of scheduled people (groomers)
 * @param appointments - Array of user appointments
 * @param isLoading - Whether data is currently loading
 * @param enableFiltering - Whether to enable working days filtering
 * @returns Array of working days for the week
 */
export const getWorkingDaysOfWeek = ({ date, people = [], appointments = [], isLoading = false, enabled = true }: GetWorkingDaysOfWeekArgs) => {
  const allWeekDays = getDaysOfWeek(date)

  // If filtering is disabled, data is loading, or no people/appointments data yet, show all days
  if (!enabled || isLoading || (people.length === 0 && appointments.length === 0)) {
    return allWeekDays
  }

  return allWeekDays.filter(day => {
    const dayOfWeek = DateHelper.toMondayWeekDay(day.day())

    // Check if any groomer is working on this day
    const hasWorkingGroomer = people.some(person => {
      const schedule = person.schedule.find(s => Number(s.day) === dayOfWeek)
      return schedule && schedule.times && schedule.times.length > 0
    })

    // Check if there are any appointments on this day
    const hasAppointments = appointments.some(groomer =>
      groomer.appointments.some(appointment =>
        day.isSame(appointment.start_at, 'day'),
      ),
    )

    return hasWorkingGroomer || hasAppointments
  })
}

export type UseWorkingWeekArgs = GetWorkingDaysOfWeekArgs

/**
 * Hook to get working days of the week with memoization
 * Only returns days where groomers are working or have appointments when filtering is enabled
 * @param date - The reference date for the week
 * @param people - Array of scheduled people (groomers)
 * @param appointments - Array of user appointments
 * @param isLoading - Whether data is currently loading
 * @param enabled - Whether to enable working days filtering
 * @returns Memoized array of working days for the week
 */
export function useWorkingWeek({ date, people = [], appointments = [], isLoading = false, enabled = true }: UseWorkingWeekArgs) {
  return useMemo(() => getWorkingDaysOfWeek({ date, people, appointments, isLoading, enabled }), [date, people, appointments, isLoading, enabled])
}
